import React, { useContext, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import { CopyIcon, FailedIcon, SuccessIcon, TimeoutIcon } from 'components/icons';
import Modal from '@mui/material/Modal';
import { ViewContext } from 'components/lib';
import { RunContext } from 'contexts/RunContext';
import { SubSimulation, SimulationNode, GeodataNode, ID } from 'types/project';
import { formatDuration } from 'utils/formatter';
import { UMButton } from '@urbanmetrix/um-kit';

type StatusRenderer = (onClick: () => void) => React.ReactNode;
type StatusModalRow = {
  key: string;
  label: string;
  value: string;
};

const getS3Prefix = (
  organizationId: ID | undefined | null,
  projectId: ID | undefined,
  gridId: ID | undefined,
  geodataId: ID | undefined,
  simulationId?: ID
) => {
  const geodataPrefix = `companies/${organizationId}/projects/${projectId}/grids/${gridId}/geodatas/${geodataId}`;
  return simulationId !== undefined && simulationId !== null
    ? `${geodataPrefix}/simulations/${simulationId}`
    : geodataPrefix;
};
const getModalMessage = (
  status: string,
  organizationId: ID | undefined | null,
  projectId: ID | undefined,
  gridId: ID | undefined,
  geodataId: ID | undefined,
  simulationId?: ID,
  subSimulationId?: ID,
  runId?: ID | null,
  failedOnStep?: string | null
) => [
  { key: 'status', label: 'Status', value: String(status ?? '') },
  { key: 'organizationId', label: 'Organization Id', value: String(organizationId ?? '') },
  { key: 'projectId', label: 'Project id', value: String(projectId ?? '') },
  { key: 'gridId', label: 'Grid id', value: String(gridId ?? '') },
  { key: 'geodataId', label: 'Geodata id', value: String(geodataId ?? '') },
  ...(simulationId !== undefined && simulationId !== null
    ? [{ key: 'simulationId', label: 'Simulation id', value: String(simulationId) }]
    : []),
  ...(subSimulationId !== undefined && subSimulationId !== null
    ? [{ key: 'subSimulationId', label: 'Subsimulation id', value: String(subSimulationId) }]
    : []),
  { key: 'runId', label: 'Run id', value: String(runId ?? 'unknown') },
  ...(failedOnStep ? [{ key: 'failedOnStep', label: 'Failed on step', value: String(failedOnStep) }] : []),
  {
    key: 's3Prefix',
    label: 'S3 prefix',
    value: getS3Prefix(organizationId, projectId, gridId, geodataId, simulationId),
  },
];

async function copyToClipboard(value: string): Promise<boolean> {
  if (!value) return false;
  await navigator.clipboard.writeText(value);
  return true;
}

function StatusModalBody({ rows, onCopySuccess }: { rows: StatusModalRow[]; onCopySuccess: () => void }) {
  return (
    <div className="status-modal-content">
      {rows.map((row) => (
        <div key={row.key} className="status-modal-row">
          <div className="status-modal-row__text">
            <strong>{row.label}:</strong> <span>{row.value}</span>
            <button
              type="button"
              className="status-modal-row__copy"
              onClick={() => {
                copyToClipboard(row.value)
                  .then((copied) => {
                    if (copied) onCopySuccess();
                  })
                  .catch((error) => console.error('Unable to copy status value:', error));
              }}
              aria-label={`Copy ${row.label}`}
              title={`Copy ${row.label}`}
            >
              <CopyIcon />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
const handleFailedClick = (onClick: () => void) => (event?: React.MouseEvent) => {
  event?.stopPropagation();
  console.log('Failed icon clicked');
  onClick(); // Call the passed function
};

const handleSuccessClick = (onClick: () => void) => (event?: React.MouseEvent) => {
  event?.stopPropagation();
  console.log('Success icon clicked');
  onClick(); // Call the passed function
};

// Status components
const statusComponents: Record<string, StatusRenderer> = {
  INITIALIZING: (onClick) => (
    <Skeleton
      variant="circular"
      width={12}
      height={12}
      onClick={handleFailedClick(onClick)}
      style={{ cursor: 'pointer' }}
    />
  ),
  SUCCEEDED: (onClick) => <SuccessIcon onClick={handleSuccessClick(onClick)} />,
  RUNNING: (onClick) => (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
      onClick={handleSuccessClick(onClick)}
    >
      <CircularProgress
        size={10}
        sx={{ color: '#000', flexShrink: 0, minWidth: 10, ml: 0.4, cursor: 'pointer' }}
        style={{ pointerEvents: 'none' }}
      />
    </span>
  ),
  FAILED: (onClick) => <FailedIcon onClick={handleFailedClick(onClick)} />,
  TIMED_OUT: (onClick) => <TimeoutIcon onClick={handleFailedClick(onClick)} />,
  ABORTED: (onClick) => <ClearIcon onClick={handleFailedClick(onClick)} style={{ cursor: 'pointer' }} />,
};

export const SubsimStatus: React.FC<{
  subSimulation: SubSimulation;
  simulation: SimulationNode;
}> = ({ subSimulation, simulation }) => {
  const viewContext = useContext(ViewContext) as {
    notification: { show: (message: string, type?: string, autoclose?: boolean, className?: string) => void };
  };
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalRows, setModalRows] = useState<StatusModalRow[]>([]);

  const status = subSimulation.status ?? '';

  const tooltipText = getTooltipText(
    subSimulation.execTime,
    status,
    subSimulation.statusMessage ?? undefined,
    subSimulation?.simType,
    subSimulation.failedOnStep ?? undefined
  );
  const hasTooltip = Boolean(tooltipText && tooltipText !== 'unknown');
  const percentage = typeof subSimulation.percentage === 'number' ? subSimulation.percentage : null;
  const expectedRemainingSeconds = subSimulation.expectedRemainingSeconds;
  const remainingSeconds =
    expectedRemainingSeconds === null || expectedRemainingSeconds === undefined
      ? null
      : Math.max(0, Math.round(Number(expectedRemainingSeconds)));
  const remainingDuration = remainingSeconds === null ? null : formatDuration(remainingSeconds, 's');
  const Component = statusComponents[status];
  const handleIconClick = () => {
    setModalRows(
      getModalMessage(
        status,
        simulation.organizationId,
        simulation.projectId,
        simulation.gridId,
        simulation.geodataId,
        subSimulation.simulationSetId,
        subSimulation.id,
        subSimulation.runId,
        subSimulation.failedOnStep
      )
    );
    setModalOpen(true);
  };
  const icon = Component ? Component(handleIconClick) : null;

  const isRunningClickable = status === 'RUNNING';

  return (
    <>
      <div
        onClick={isRunningClickable ? handleIconClick : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 20,
          cursor: isRunningClickable ? 'pointer' : undefined,
        }}
      >
        {status === 'RUNNING' && percentage !== null && percentage < 99 && (
          <span style={{ cursor: 'pointer' }}>{`${Math.floor(percentage)}%`}</span>
        )}
        {status === 'RUNNING' && remainingSeconds !== null && percentage !== null && percentage < 99 && (
          <span style={{ cursor: 'pointer' }}>{`-${remainingDuration || `${remainingSeconds}s`}`}</span>
        )}
        {status === 'RUNNING' && percentage !== null && percentage >= 99 ? (
          <span style={{ cursor: 'pointer' }}>Finalizing...</span>
        ) : icon ? (
          <Tooltip
            title={<div className="status-tooltip-content">{tooltipText || 'No information available'}</div>}
            slotProps={{ tooltip: { className: 'status-tooltip' } }}
            disableHoverListener={!hasTooltip}
            disableFocusListener={!hasTooltip}
            disableTouchListener={!hasTooltip}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                cursor: 'pointer',
              }}
            >
              {icon}
            </span>
          </Tooltip>
        ) : null}
      </div>
      <Modal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        className="custom-modal"
        slotProps={{ backdrop: { onClick: (event) => event.stopPropagation() } }}
      >
        <div className="status-modal" onClick={(event) => event.stopPropagation()}>
          <h2 id="modal-title" className="status-modal-title">
            Status Information
          </h2>
          <div id="modal-description">
            <StatusModalBody
              rows={modalRows}
              onCopySuccess={() => viewContext.notification.show('Text copied!', 'success', true, 'toast')}
            />
          </div>
          <div className="status-modal-actions">
            <UMButton text="Close" onClick={() => setModalOpen(false)} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export const GeodataStatus: React.FC<{
  infoObjectStatuses: Record<ID, string>;
  geodataTasks: Record<ID, any>;
  geodata: GeodataNode;
  projectId: ID;
}> = ({ infoObjectStatuses, geodataTasks, geodata, projectId }) => {
  const viewContext = useContext(ViewContext) as {
    notification: { show: (message: string, type?: string, autoclose?: boolean, className?: string) => void };
  };
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalRows, setModalRows] = useState<StatusModalRow[]>([]);
  const { allGeodatas } = useContext(RunContext);

  const handleIconClick = () => {
    setModalRows(
      getModalMessage(
        status,
        geodata.organizationId ?? (geodata as GeodataNode & { account_id?: ID | null }).account_id ?? null,
        projectId,
        geodata.gridId,
        geodata.id,
        undefined,
        undefined,
        latestGeodata.runId,
        latestGeodata.failedOnStep
      )
    );
    setModalOpen(true);
  };

  const id = geodata.id;
  const latestGeodata = allGeodatas[id] ?? geodata;
  const status = infoObjectStatuses[id] ?? '';
  const tooltipText = getTooltipText(
    latestGeodata.execTime,
    status,
    latestGeodata.statusMessage ?? undefined,
    undefined,
    latestGeodata.failedOnStep ?? undefined
  );
  const Component = statusComponents[status];

  const icon = Component ? Component(handleIconClick) : null;

  return icon ? (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          minHeight: 20,
        }}
      >
        {status !== 'SUCCEEDED' &&
          geodataTasks[id] &&
          geodataTasks[id].tasks_succeeded !== undefined &&
          geodataTasks[id].tasks_total !== undefined && (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                handleIconClick();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  handleIconClick();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {`${geodataTasks[id].tasks_succeeded} / ${geodataTasks[id].tasks_total}`}
            </span>
          )}
        <Tooltip
          title={<div className="status-tooltip-content">{tooltipText || 'No information available'}</div>}
          slotProps={{ tooltip: { className: 'status-tooltip' } }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              cursor: 'pointer',
            }}
          >
            {icon}
          </span>
        </Tooltip>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        className="custom-modal"
        slotProps={{ backdrop: { onClick: (event) => event.stopPropagation() } }}
      >
        <div className="status-modal" onClick={(event) => event.stopPropagation()}>
          <h2 id="modal-title" className="status-modal-title">
            Status Information
          </h2>
          <div id="modal-description">
            <StatusModalBody
              rows={modalRows}
              onCopySuccess={() => viewContext.notification.show('Text copied!', 'success', true, 'toast')}
            />
          </div>
          <div className="status-modal-actions">
            <UMButton text="Close" onClick={() => setModalOpen(false)} />
          </div>
        </div>
      </Modal>
    </>
  ) : null;
};

const TIMEOUT_GENERIC_MESSAGE =
  'Simulation exceeded maximum allowed time, try to increase or decrease simulation accuracy; if issue persists contact support.';
const TIMEOUT_WIND_MESSAGE =
  'Simulation exceeded maximum allowed time, try to increase or decrease simulation accuracy or the Radius of the Wind Analysis; if issue persists contact support.';

function getTimeoutErrorMessage(simulationType?: string) {
  switch (String(simulationType ?? '').toLowerCase()) {
    case 'wind':
      return TIMEOUT_WIND_MESSAGE;
    default:
      return TIMEOUT_GENERIC_MESSAGE;
  }
}

function getTooltipText(
  durationMs: number | null | undefined,
  status?: string,
  errorMessage?: string,
  simulationType?: string,
  failedOnStep?: string | null
) {
  if (status === 'FAILED') {
    const stepText = failedOnStep ? `Failed on step: ${failedOnStep}` : null;
    if (stepText && errorMessage) return `${stepText}\n${errorMessage}`;
    return stepText || errorMessage || status;
  }
  if (status === 'TIMED_OUT') {
    const durationText = durationMs === null || durationMs === undefined ? null : formatDuration(durationMs);
    const message = getTimeoutErrorMessage(simulationType);
    return durationText ? `${durationText} - ${message}` : message;
  }

  if (durationMs === null || durationMs === undefined) {
    return status || 'unknown';
  }

  return formatDuration(durationMs) || status || 'unknown';
}
